import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

export const inboundVerificationSchema = z.object({
  message_id: z.string().uuid(),
  host: z.enum(['gmail', 'outlook']),
  code: z.string().nullable(),
  url: z.string().url().startsWith('https://').nullable(),
  from_email: z.string().email().nullable(),
});

export const inboundMailboxSchema = z.object({
  address: z.string().email().nullable(),
  status: z.enum(['active', 'revoked']),
  last_accepted_at: z.string().nullable(),
  health: z.enum(['waiting', 'ok', 'unparsed', 'verification', 'revoked', 'error']),
  verification_code: z.string().nullable(),
  verification_url: z.string().url().startsWith('https://').nullable(),
  verification_host: z.enum(['gmail', 'outlook']).nullable(),
  verifications: z.array(inboundVerificationSchema).default([]),
  gmail_addresses: z.array(z.string().email()).default([]),
});

export type InboundMailbox = z.infer<typeof inboundMailboxSchema>;
export type InboundVerification = z.infer<typeof inboundVerificationSchema>;

const queryKey = ['inbound-mailbox'] as const;

export function useInboundMailbox() {
  return useQuery({
    queryKey,
    queryFn: async () => {
      const response = await client.get('/api/inbound/mailbox');
      return parseEnvelope(inboundMailboxSchema, response.data).data;
    },
  });
}

export function useRevokeInboundMailbox() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await client.delete('/api/inbound/mailbox');
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useDismissInboundVerification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (messageId: string) => {
      await client.delete(`/api/inbound/mailbox/verifications/${messageId}`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useRemintInboundMailbox() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await client.post('/api/inbound/mailbox');
      return parseEnvelope(inboundMailboxSchema, response.data).data;
    },
    onSuccess: (mailbox) => {
      queryClient.setQueryData(queryKey, mailbox);
    },
  });
}
